import { useState, useCallback } from 'react';
import { api } from '../utils/api';
import type { DutchVerb, VerbExercise, VerbProgress } from '@homeapp/shared';

interface VerbProgressOverview {
  totalVerbs: number;
  practiced: number;
  totalCorrect: number;
  totalIncorrect: number;
  accuracy: number;
  verbs: Array<{
    verbId: string;
    infinitive: string;
    difficulty: number;
    progress: VerbProgress | null;
  }>;
}

interface AnswerResult {
  correct: boolean;
  expectedAnswer: string;
  feedback: string;
  progress: VerbProgress;
}

export function useVerbTrainer() {
  const [exercise, setExercise] = useState<VerbExercise | null>(null);
  const [progressOverview, setProgressOverview] = useState<VerbProgressOverview | null>(null);
  const [verbs, setVerbs] = useState<(DutchVerb & { progress?: VerbProgress })[]>([]);
  const [loading, setLoading] = useState(false);
  const [exerciseLoading, setExerciseLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<AnswerResult | null>(null);

  const fetchVerbs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<{ verbs: (DutchVerb & { progress?: VerbProgress })[] }>('/verbs/list');
      setVerbs(data.verbs);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load verbs');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProgress = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<VerbProgressOverview>('/verbs/progress');
      setProgressOverview(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load progress');
    } finally {
      setLoading(false);
    }
  }, []);

  const nextExercise = useCallback(async () => {
    setExerciseLoading(true);
    setError(null);
    setLastResult(null);
    try {
      const data = await api.get<{ exercise: VerbExercise | null; message?: string }>('/verbs/exercise');
      setExercise(data.exercise);
      if (!data.exercise && data.message) {
        setError(data.message);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load exercise');
    } finally {
      setExerciseLoading(false);
    }
  }, []);

  const submitAnswer = useCallback(async (
    verbId: string,
    answer: string,
    exerciseType: string,
    person?: string,
    tenseQuestion?: string,
  ) => {
    try {
      const data = await api.post<AnswerResult>(`/verbs/exercise/${verbId}/answer`, {
        answer,
        exerciseType,
        person,
        tenseQuestion,
      });
      setLastResult(data);
      return data;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to submit answer');
      return null;
    }
  }, []);

  const generateVerb = useCallback(async (infinitive: string) => {
    try {
      const data = await api.post<{ verb: DutchVerb }>('/verbs/generate', { infinitive });
      return data.verb;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to generate verb');
      return null;
    }
  }, []);

  const saveVerb = useCallback(async (verb: Partial<DutchVerb>) => {
    try {
      const data = await api.post<{ verb: DutchVerb }>('/verbs', { verb });
      return data.verb;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save verb');
      return null;
    }
  }, []);

  const updateVerb = useCallback(async (verbId: string, verb: Partial<DutchVerb>) => {
    try {
      const data = await api.put<{ verb: DutchVerb }>(`/verbs/${verbId}`, { verb });
      return data.verb;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update verb');
      return null;
    }
  }, []);

  const deleteVerb = useCallback(async (verbId: string) => {
    try {
      await api.delete(`/verbs/${verbId}`);
      setVerbs(prev => prev.filter(v => v.verbId !== verbId));
      return true;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete verb');
      return false;
    }
  }, []);

  const explainWord = useCallback(async (word: string) => {
    try {
      const data = await api.post<{ translation?: string; dutch: string; english: string }>('/verbs/explain', { word });
      return data;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to explain word');
      return null;
    }
  }, []);

  return {
    exercise,
    progressOverview,
    verbs,
    loading,
    exerciseLoading,
    error,
    lastResult,
    fetchVerbs,
    fetchProgress,
    nextExercise,
    submitAnswer,
    generateVerb,
    saveVerb,
    updateVerb,
    deleteVerb,
    explainWord,
    setError,
  };
}
